import { getCommonWidget } from "../../dashWidgetHelper";
import {
  commonWidgets,
} from "../commonWidgetsProfuturo";

// IDs de momentos críticos (staticDashboards.ts)
const CM_CHAT_HUMANO = 10370
const CM_CHAT_ALBA = 10351
const CM_SUCURSAL = 13604

// question_id de la pregunta "_mapping" (Workflow, TEXT) que trae el canal
// de origen de cada encuesta close-the-loop respondida.
const WORKFLOW_QUESTION_ID = 173242651

// Valores crudos de respuesta de la pregunta Workflow para cada canal.
const WORKFLOW_CHATHUMANO = 'chathumano'
const WORKFLOW_ALBA = 'alba'
const WORKFLOW_SUCURSAL = 'sucursal'

const WORKFLOW_TO_CRITICAL_MOMENT: Record<string, number> = {
  [WORKFLOW_CHATHUMANO]: CM_CHAT_HUMANO,
  [WORKFLOW_ALBA]: CM_CHAT_ALBA,
  [WORKFLOW_SUCURSAL]: CM_SUCURSAL,
}

// Filtra la consulta del indicador para que solo cuente las respuestas de
// close-the-loop cuyo Workflow (canal de origen) sea el indicado -> así cada
// sección (Chat Humano/Alba/Sucursal) muestra info real y dinámica solo de
// SUS encuestas, no del total ni de otro canal.
const channelFilterExtras = (workflowValue: string) => ({
  responseValues: { [WORKFLOW_QUESTION_ID]: [workflowValue] }
})

const CHANNEL_LABELS: Record<string, string> = {
  chathumano: 'Chat Humano',
  alba: 'Chat Alba',
  sucursal: 'Sucursal',
}

// Traduce los valores crudos de la pregunta "_mapping" (chathumano/alba/
// sucursal) a etiquetas legibles para el pie de "Encuestas Close the Loop
// por canal", SUMANDO por canal normalizado (minúsculas + trim) -> si el
// backend trae el mismo canal con variantes de mayúsculas/espacios en
// distintas respuestas (ej. "chathumano" y "ChatHumano"), no aparecen como
// rebanadas separadas: se agregan en una sola. Un valor no mapeado se
// agrupa aparte, mostrando el texto crudo tal cual, en vez de perderlo.
const channelCountMapper = (data: any) => {
  const rows: any[] = Array.isArray(data) ? data : []

  const totals: Record<string, number> = {}
  const labels: Record<string, string> = {}

  rows.forEach((row: any) => {
    if (row?.group === undefined || row.group === null) {
      return
    }
    const rawGroup = row.group.toString().trim()
    if (rawGroup === '') {
      return
    }
    const key = rawGroup.toLowerCase()
    const count = Number(row?.count ?? row?.value ?? 0)

    totals[key] = (totals[key] ?? 0) + count
    labels[key] = CHANNEL_LABELS[key] ?? rawGroup
  })

  // `value` es lo que el chart interpreta como el % de la rebanada (y le
  // agrega el símbolo "%"); `count` es el conteo absoluto que se muestra
  // aparte (showCountLabel). Si aquí ponemos el conteo crudo en `value`,
  // con una sola rebanada coincide por casualidad con el %, pero con más
  // de un canal ya no cuadra -> hay que calcular el % real sobre el total.
  const totalCount = Object.values(totals).reduce((sum, n) => sum + n, 0)

  return Object.keys(totals).map((key, idx) => ({
    id: idx,
    group: key,
    label: labels[key],
    value: totalCount > 0 ? (totals[key] / totalCount) * 100 : 0,
    count: totals[key],
  }))
}

// Extrae {group, count} de la respuesta de un indicador agrupado por
// responseValue (groupBy: 'responseValue', groupByResponseValueId: X).
const extractGroups = (data: any): { group: string, count: number }[] => {
  const rows: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.[0])
      ? data[0]
      : []

  return rows
    .map((row: any) => ({
      group: (row?.group ?? row?.groupName ?? row?.value ?? '').toString().trim(),
      count: Number(row?.count ?? row?.value ?? 0),
    }))
    .filter((row) => row.group !== '' && row.count > 0)
}

// "Gate": antes de pintar el dashboard, averigua qué canal(es) tienen
// encuestas close-the-loop respondidas en el rango de fechas/filtros
// actuales -> así solo se muestra la sección del canal real, en vez de
// las 3 siempre. Recibe `api` como parámetro (ver Entities.ts) porque este
// archivo se carga muy temprano en el árbol de config y un import directo
// de '@services/api' aquí genera una dependencia circular.
const gate = {
  fetch: async (filters: FiltersUI, filtersData: Record<string, any> | undefined, api: any): Promise<Record<string, any>> => {
    let channelsData: any

    try {
      const channelsResp = await api.indicators.data('indicators/close_the_loop_si_no/values', filters, filtersData, {
        formula: 'count-text',
        groupNameFormat: 'description',
        groupBy: 'responseValue',
        groupByResponseValueId: WORKFLOW_QUESTION_ID,
      })
      channelsData = channelsResp?.ok ? channelsResp.data : undefined
    } catch (e) {
      channelsData = undefined
    }

    const channels = new Set<number>()
    extractGroups(channelsData).forEach(({ group }) => {
      const cmId = WORKFLOW_TO_CRITICAL_MOMENT[group.toLowerCase()]
      if (cmId !== undefined) {
        channels.add(cmId)
      }
    })

    return {
      closeTheLoopChannels: Array.from(channels),
    }
  }
}

const renderChatHumano = (filters: FiltersUI, filtersData?: Record<string, any>) =>
  (filtersData?.closeTheLoopChannels ?? []).includes(CM_CHAT_HUMANO)
const renderChatAlba = (filters: FiltersUI, filtersData?: Record<string, any>) =>
  (filtersData?.closeTheLoopChannels ?? []).includes(CM_CHAT_ALBA)
const renderSucursal = (filters: FiltersUI, filtersData?: Record<string, any>) =>
  (filtersData?.closeTheLoopChannels ?? []).includes(CM_SUCURSAL)

export default {
  title: 'Close the loop Dashboard',
  description: 'Close the loop Dashboard',
  backgroundClassName: (filters: FiltersUI) => 'epsilon-hero-bg',
  gate: gate,
  widgets: (filters: FiltersUI, filtersData?: Record<string, any>) => [
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'pieGenericMediumSiNo',
      title: '¿Pudiste resolver tu consulta/inquietud?',
      helpText: '¿Pudiste resolver tu consulta/inquietud?',
      position: [0, 0],
      size: [2, 2],
      key: 'ctl-total-sino',
      indicator: 'close_the_loop_si_no',
      extras: {
        groupNameFormat: 'description',
        formula: 'count-text'
      },
    }),
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'countNumberSemicircle',
      title: 'Satisfacción',
      position: [2, 0],
      size: [2, 2],
      key: 'ctl-total-csat',
      indicator: 'close_the_loop_csat',
      helpText: '¿Qué tan satisfecho estás con la llamada que recibiste de Profuturo?',
      historic: {
        type: 'line',
        formula: 'wavg',
        formulaConfig: 'wavg-top',
      }
    }),

    // Distribución de encuestas Close the Loop respondidas por canal de
    // origen (Chat Humano / Chat Alba / Sucursal), usando el mismo dato que
    // ya usa el `gate` para decidir qué sección mostrar.
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'pieGenericMedium',
      title: 'Encuestas Close the Loop por canal',
      helpText: '¿De qué canal proviene cada encuesta de close the loop respondida en el periodo?',
      position: [0, 1],
      size: [2, 2],
      key: 'ctl-by-channel',
      indicator: 'close_the_loop_si_no',
      mapper: channelCountMapper,
      // Muestra el conteo real (ej. "21") además del % en la etiqueta/tooltip,
      // en vez de solo el % (que sin esto se ve como si "21" fuera un
      // porcentaje literal cuando en realidad es la cantidad de encuestas).
      extraConfigs: {
        showCountLabel: true,
      },
      extras: {
        formula: 'count-text',
        groupNameFormat: 'description',
        groupBy: 'responseValue',
        groupByResponseValueId: WORKFLOW_QUESTION_ID,
      },
    }),

    // ---- Chat Humano (10370) ----
    {
      title: 'Chat humano',
      position: [0, 2],
      size: [4, 1],
      type: 'label',
      config: { color: '#B7AADC' },
      render: renderChatHumano,
    },
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'pieGenericMediumSiNo',
      title: '¿Pudiste resolver tu consulta/inquietud?',
      position: [0, 3],
      size: [2, 2],
      helpText: '¿Pudiste resolver tu consulta/inquietud?',
      key: 'ctl-chathumano-sino',
      indicator: 'close_the_loop_si_no',
      extras: {
        groupNameFormat: 'description',
        formula: 'count-text',
        ...channelFilterExtras(WORKFLOW_CHATHUMANO),
      },
      render: renderChatHumano,
    }),
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'countNumberSemicircle',
      title: 'Satisfacción',
      position: [2, 3],
      size: [2, 2],
      key: 'ctl-chathumano-csat',
      indicator: 'close_the_loop_csat',
      helpText: '¿Qué tan satisfecho estás con la llamada que recibiste de Profuturo?',
      extras: channelFilterExtras(WORKFLOW_CHATHUMANO),
      historic: {
        type: 'line',
        formula: 'wavg',
        formulaConfig: 'wavg-top',
      },
      render: renderChatHumano,
    }),

    // ---- Chat Alba (10351) ----
    {
      title: 'Chat Alba',
      position: [0, 5],
      size: [4, 1],
      type: 'label',
      config: { color: '#B7AADC' },
      render: renderChatAlba,
    },
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'pieGenericMediumSiNo',
      title: '¿Pudiste resolver tu consulta/inquietud?',
      position: [0, 6],
      size: [2, 2],
      helpText: '¿Pudiste resolver tu consulta/inquietud?',
      key: 'ctl-alba-sino',
      indicator: 'close_the_loop_si_no',
      extras: {
        groupNameFormat: 'description',
        formula: 'count-text',
        ...channelFilterExtras(WORKFLOW_ALBA),
      },
      render: renderChatAlba,
    }),
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'countNumberSemicircle',
      title: 'Satisfacción',
      position: [2, 6],
      size: [2, 2],
      key: 'ctl-alba-csat',
      indicator: 'close_the_loop_csat',
      helpText: '¿Qué tan satisfecho estás con la llamada que recibiste de Profuturo?',
      extras: channelFilterExtras(WORKFLOW_ALBA),
      historic: {
        type: 'line',
        formula: 'wavg',
        formulaConfig: 'wavg-top',
      },
      render: renderChatAlba,
    }),

    // ---- Sucursal (13604) ----
    {
      title: 'Sucursal',
      position: [0, 8],
      size: [4, 1],
      type: 'label',
      config: { color: '#B7AADC' },
      render: renderSucursal,
    },
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'pieGenericMediumSiNo',
      title: '¿Pudiste resolver tu consulta/inquietud?',
      position: [0, 9],
      size: [2, 2],
      helpText: '¿Pudiste resolver tu consulta/inquietud?',
      key: 'ctl-sucursal-sino',
      indicator: 'close_the_loop_si_no',
      extras: {
        groupNameFormat: 'description',
        formula: 'count-text',
        ...channelFilterExtras(WORKFLOW_SUCURSAL),
      },
      render: renderSucursal,
    }),
    getCommonWidget({
      commonWidgets: commonWidgets,
      name: 'countNumberSemicircle',
      title: 'Satisfacción',
      position: [2, 9],
      size: [2, 2],
      key: 'ctl-sucursal-csat',
      indicator: 'close_the_loop_csat',
      helpText: '¿Qué tan satisfecho estás con la llamada que recibiste de Profuturo?',
      extras: channelFilterExtras(WORKFLOW_SUCURSAL),
      historic: {
        type: 'line',
        formula: 'wavg',
        formulaConfig: 'wavg-top',
      },
      render: renderSucursal,
    }),
  ]
} as DasboardConfig
